import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import { useNotification } from '../../contexts/NotificationContext';

const STATUS_OPTIONS = ['present', 'absent', 'late', 'sick'];

const STATUS_STYLES = {
  present: { bg: '#dcfce7', color: '#15803d', border: '#bbf7d0' },
  absent:  { bg: '#fee2e2', color: '#b91c1c', border: '#fecaca' },
  late:    { bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  sick:    { bg: '#dbeafe', color: '#1d4ed8', border: '#bfdbfe' },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.present;
  return (
    <span style={{
      display: 'inline-block',
      padding: '0.2rem 0.65rem',
      borderRadius: '999px',
      fontSize: '0.72rem',
      fontWeight: 700,
      fontFamily: "'DM Sans', sans-serif",
      background: s.bg,
      color: s.color,
      border: `1px solid ${s.border}`,
      textTransform: 'capitalize',
    }}>
      {status}
    </span>
  );
}

function formatDateForInput(d) {
  if (!d) return '';
  const dt = typeof d === 'string' ? new Date(d) : d;
  return dt.toISOString().slice(0, 10);
}

function todayStr() {
  return formatDateForInput(new Date());
}

function AttendanceContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const { user } = useAuth();

  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [artists, setArtists] = useState([]);
  const [managers, setManagers] = useState([]);
  const [workerAttendance, setWorkerAttendance] = useState([]);
  const [bmAttendance, setBmAttendance] = useState([]);
  const [byBranchData, setByBranchData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('workers'); // workers | branch-managers | review
  const [date, setDate] = useState(todayStr());
  const [dateFrom, setDateFrom] = useState(todayStr());
  const [dateTo, setDateTo] = useState(todayStr());
  const [workerForm, setWorkerForm] = useState({}); // { artistId: { status, hours_worked } }
  const [bmForm, setBmForm] = useState({}); // { userId: { status, hours_worked } }

  const branchId = user?.branch_id;
  const isShopManager = user?.role === 'shop_manager' || user?.role === 'salon_manager';
  const isBranchManager = user?.role === 'branch_manager';
  const effectiveBranchId = selectedBranchId || branchId;

  useEffect(() => {
    if (isShopManager) {
      api.getManagerBranches().then(setBranches).catch(() => setBranches([]));
      api.getAssignableBranchManagers().then(setManagers).catch(() => setManagers([]));
    }
  }, [isShopManager]);

  useEffect(() => {
    setLoading(true);
    api.getManagerArtists()
      .then((data) => {
        setArtists(Array.isArray(data) ? data : []);
      })
      .catch(() => setArtists([]))
      .finally(() => setLoading(false));
  }, [effectiveBranchId]);

  useEffect(() => {
    if (!effectiveBranchId && isBranchManager) return;
    setLoading(true);
    api.getManagerWorkerAttendance(effectiveBranchId, date, date)
      .then((data) => {
        setWorkerAttendance(Array.isArray(data) ? data : []);
        const form = {};
        data.forEach((a) => {
          form[a.artist_id] = { status: a.status, hours_worked: a.hours_worked ?? '' };
        });
        setWorkerForm(form);
      })
      .catch(() => { setWorkerAttendance([]); setWorkerForm({}); })
      .finally(() => setLoading(false));
  }, [effectiveBranchId, date]);

  useEffect(() => {
    if (isBranchManager) return;
    api.getManagerBranchManagerAttendance(selectedBranchId || null, date, date)
      .then((data) => {
        setBmAttendance(Array.isArray(data) ? data : []);
        const form = {};
        data.forEach((a) => {
          form[a.user_id] = { status: a.status, hours_worked: a.hours_worked ?? '' };
        });
        setBmForm(form);
      })
      .catch(() => { setBmAttendance([]); setBmForm({}); });
  }, [isShopManager, selectedBranchId, date]);

  useEffect(() => {
    if (!isShopManager || activeTab !== 'review') return;
    api.getManagerAttendanceByBranch(dateFrom, dateTo)
      .then(setByBranchData)
      .catch(() => setByBranchData([]));
  }, [isShopManager, activeTab, dateFrom, dateTo]);

  const filteredArtists = artists.filter((a) => !effectiveBranchId || a.branch_id === effectiveBranchId);
  const branchManagersForBranch = managers.filter((m) => !selectedBranchId || m.branch_id === selectedBranchId);

  useEffect(() => {
    if (filteredArtists.length && Object.keys(workerForm).length === 0 && !loading) {
      const form = {};
      filteredArtists.forEach((a) => {
        const existing = workerAttendance.find((w) => w.artist_id === a.id);
        form[a.id] = existing
          ? { status: existing.status, hours_worked: existing.hours_worked ?? '' }
          : { status: 'present', hours_worked: '' };
      });
      setWorkerForm(form);
    }
  }, [filteredArtists, workerAttendance, loading]);

  const updateWorkerForm = (artistId, field, value) => {
    setWorkerForm((prev) => ({
      ...prev,
      [artistId]: {
        ...(prev[artistId] || { status: 'present', hours_worked: '' }),
        [field]: value,
      },
    }));
  };

  const updateBmForm = (userId, field, value) => {
    setBmForm((prev) => ({
      ...prev,
      [userId]: {
        ...(prev[userId] || { status: 'present', hours_worked: '' }),
        [field]: value,
      },
    }));
  };

  const saveWorkerAttendance = async () => {
    if (!effectiveBranchId) {
      showAlert(t('attendance.selectBranch') || 'Select a branch first');
      return;
    }
    setSaving(true);
    try {
      for (const a of filteredArtists) {
        const f = workerForm[a.id] || { status: 'present', hours_worked: '' };
        const payload = {
          artist_id: a.id,
          date,
          status: f.status,
          hours_worked: f.hours_worked ? parseFloat(f.hours_worked) : null,
        };
        const existing = workerAttendance.find((w) => w.artist_id === a.id);
        if (existing) {
          await api.updateManagerWorkerAttendance(existing.id, { status: payload.status, hours_worked: payload.hours_worked });
        } else {
          await api.createManagerWorkerAttendance(payload);
        }
      }
      showAlert(t('attendance.saved') || 'Attendance saved');
      api.getManagerWorkerAttendance(effectiveBranchId, date, date).then((data) => {
        setWorkerAttendance(Array.isArray(data) ? data : []);
      });
    } catch (err) {
      showAlert(err.message || (t('attendance.saveFailed') || 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const saveBmAttendance = async () => {
    setSaving(true);
    try {
      for (const m of branchManagersForBranch) {
        const f = bmForm[m.id] || { status: 'present', hours_worked: '' };
        const payload = {
          user_id: m.id,
          date,
          status: f.status,
          hours_worked: f.hours_worked ? parseFloat(f.hours_worked) : null,
        };
        const existing = bmAttendance.find((b) => b.user_id === m.id);
        if (existing) {
          await api.updateManagerBranchManagerAttendance(existing.id, { status: payload.status, hours_worked: payload.hours_worked });
        } else {
          await api.createManagerBranchManagerAttendance(payload);
        }
      }
      showAlert(t('attendance.saved') || 'Attendance saved');
      api.getManagerBranchManagerAttendance(selectedBranchId || null, date, date).then((data) => {
        setBmAttendance(Array.isArray(data) ? data : []);
      });
    } catch (err) {
      showAlert(err.message || (t('attendance.saveFailed') || 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const markAllPresent = () => {
    const form = {};
    filteredArtists.forEach(a => {
      form[a.id] = { status: 'present', hours_worked: workerForm[a.id]?.hours_worked ?? '' };
    });
    setWorkerForm(form);
  };

  const tabStyle = (active) => ({
    background: 'none',
    border: 'none',
    color: active ? T.orange : T.muted,
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    fontWeight: active ? 600 : 500,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.9rem',
    borderBottom: active ? `2px solid ${T.orange}` : '2px solid transparent',
    transition: 'color 0.15s',
  });

  return (
    <AdminLayout role="manager">
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={S.pageTitle}>{t('attendance.title') || 'Attendance'}</h1>
        <div style={{ width: '40px', height: '3px', background: T.orange, borderRadius: '2px', marginTop: '0.3rem' }} />
      </div>

      {isShopManager && (
        <div style={{ display: 'flex', gap: 0, marginBottom: '1.5rem', borderBottom: `1px solid ${T.border}` }}>
          <button style={tabStyle(activeTab === 'workers')} onClick={() => setActiveTab('workers')}>
            {t('attendance.workerAttendance') || 'Worker Attendance'}
          </button>
          <button style={tabStyle(activeTab === 'branch-managers')} onClick={() => setActiveTab('branch-managers')}>
            {t('attendance.branchManagerAttendance') || 'Branch Manager Attendance'}
          </button>
          <button style={tabStyle(activeTab === 'review')} onClick={() => setActiveTab('review')}>
            {t('attendance.reviewByBranch') || 'Review by Branch'}
          </button>
        </div>
      )}

      {(activeTab === 'workers' || isBranchManager) && (
        <div style={S.card}>
          {isShopManager && branches.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, marginBottom: '0.4rem' }}>{t('attendance.branch') || 'Branch'}</label>
              <select
                value={selectedBranchId ?? ''}
                onChange={(e) => setSelectedBranchId(e.target.value ? parseInt(e.target.value) : null)}
                style={{ ...S.select, width: 'auto' }}
              >
                <option value="">{t('attendance.allBranches') || 'All Branches'}</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          )}
          <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.78rem', color: T.muted }}>{t('attendance.date') || 'Date'}</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...S.input, width: 'auto', marginBottom: 0 }} />
            <button
              style={{ ...S.btnOutline, marginBottom: 0 }}
              onClick={markAllPresent}
              disabled={filteredArtists.length === 0}
            >
              ✓ {t('attendance.markAllPresent') || 'Mark All Present'}
            </button>
            <button
              style={{ ...S.btnOrange, marginBottom: 0 }}
              onClick={saveWorkerAttendance}
              disabled={saving || !effectiveBranchId || filteredArtists.length === 0}
            >
              {saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
            </button>
          </div>
          {loading ? (
            <p style={{ color: T.muted }}>{t('common.loading')}</p>
          ) : filteredArtists.length === 0 ? (
            <p style={{ color: T.muted }}>{t('attendance.noWorkers') || 'No workers in this branch.'}</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>{t('attendance.worker') || 'Worker'}</th>
                    <th style={S.th}>{t('attendance.status') || 'Status'}</th>
                    <th style={S.th}>{t('attendance.hoursWorked') || 'Hours'}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredArtists.map((a) => (
                    <tr key={a.id}>
                      <td style={S.td}>{a.name}</td>
                      <td style={S.td}>
                        <select
                          value={(workerForm[a.id] || {}).status || 'present'}
                          onChange={(e) => updateWorkerForm(a.id, 'status', e.target.value)}
                          style={{ ...S.select, marginBottom: 0, minWidth: 100 }}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>{t(`attendance.status_${s}`) || s}</option>
                          ))}
                        </select>
                      </td>
                      <td style={S.td}>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          placeholder="—"
                          value={(workerForm[a.id] || {}).hours_worked ?? ''}
                          onChange={(e) => updateWorkerForm(a.id, 'hours_worked', e.target.value)}
                          style={{ ...S.input, width: 80, marginBottom: 0 }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'branch-managers' && isShopManager && (
        <div style={S.card}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, marginBottom: '0.4rem' }}>{t('attendance.branch') || 'Branch'}</label>
            <select value={selectedBranchId ?? ''} onChange={(e) => setSelectedBranchId(e.target.value ? parseInt(e.target.value) : null)} style={{ ...S.select, width: 'auto' }}>
              <option value="">{t('attendance.allBranches') || 'All Branches'}</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <label style={{ fontSize: '0.78rem', color: T.muted }}>{t('attendance.date') || 'Date'}</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...S.input, width: 'auto', marginBottom: 0 }} />
            <button style={{ ...S.btnOrange, marginBottom: 0 }} onClick={saveBmAttendance} disabled={saving || branchManagersForBranch.length === 0}>
              {saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
            </button>
          </div>
          {branchManagersForBranch.length === 0 ? (
            <p style={{ color: T.muted }}>{t('attendance.noBranchManagers') || 'No branch managers assigned.'}</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>{t('attendance.branchManager') || 'Branch Manager'}</th>
                    <th style={S.th}>{t('attendance.status') || 'Status'}</th>
                    <th style={S.th}>{t('attendance.hoursWorked') || 'Hours'}</th>
                  </tr>
                </thead>
                <tbody>
                  {branchManagersForBranch.map((m) => (
                    <tr key={m.id}>
                      <td style={S.td}>{m.name} {m.email && <span style={{ color: T.muted, fontSize: '0.8rem' }}>({m.email})</span>}</td>
                      <td style={S.td}>
                        <select
                          value={(bmForm[m.id] || {}).status || 'present'}
                          onChange={(e) => updateBmForm(m.id, 'status', e.target.value)}
                          style={{ ...S.select, marginBottom: 0, minWidth: 100 }}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>{t(`attendance.status_${s}`) || s}</option>
                          ))}
                        </select>
                      </td>
                      <td style={S.td}>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          placeholder="—"
                          value={(bmForm[m.id] || {}).hours_worked ?? ''}
                          onChange={(e) => updateBmForm(m.id, 'hours_worked', e.target.value)}
                          style={{ ...S.input, width: 80, marginBottom: 0 }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'review' && isShopManager && (
        <div style={S.card}>
          <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.78rem', color: T.muted }}>{t('attendance.dateFrom') || 'From'}</span>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ ...S.input, width: 'auto', marginBottom: 0 }} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.78rem', color: T.muted }}>{t('attendance.dateTo') || 'To'}</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ ...S.input, width: 'auto', marginBottom: 0 }} />
            </label>
          </div>
          {byBranchData.length === 0 ? (
            <p style={{ color: T.muted }}>{t('attendance.noData') || 'No attendance data for this period.'}</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {byBranchData.map((br) => (
                <div key={br.branch_id} style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ padding: '0.75rem 1rem', background: T.bg, borderBottom: `1px solid ${T.border}`, fontWeight: 600, color: T.white }}>
                    {br.branch_name}
                    <span style={{ marginLeft: '0.75rem', fontSize: '0.85rem', fontWeight: 500, color: T.muted }}>
                      {t('attendance.workersPresent') || 'Workers present'}: {br.summary?.workers_present ?? 0} / {t('attendance.branchManagersPresent') || 'Managers present'}: {br.summary?.branch_managers_present ?? 0}
                    </span>
                  </div>
                  <div style={{ padding: '1rem' }}>
                    {br.workers?.length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.78rem', color: T.muted, marginBottom: '0.5rem', textTransform: 'uppercase' }}>{t('attendance.workers') || 'Workers'}</div>
                        <table style={{ ...S.table, marginBottom: 0 }}>
                          <thead><tr><th style={S.th}>{t('attendance.name') || 'Name'}</th><th style={S.th}>{t('attendance.date') || 'Date'}</th><th style={S.th}>{t('attendance.status') || 'Status'}</th><th style={S.th}>{t('attendance.hoursWorked') || 'Hours'}</th></tr></thead>
                          <tbody>
                            {br.workers.map((w, i) => (
                              <tr key={i}><td style={S.td}>{w.name}</td><td style={S.td}>{w.date}</td><td style={S.td}><StatusPill status={w.status} /></td><td style={S.td}>{w.hours_worked ?? '—'}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {br.branch_managers?.length > 0 && (
                      <div>
                        <div style={{ fontSize: '0.78rem', color: T.muted, marginBottom: '0.5rem', textTransform: 'uppercase' }}>{t('attendance.branchManagers') || 'Branch Managers'}</div>
                        <table style={{ ...S.table, marginBottom: 0 }}>
                          <thead><tr><th style={S.th}>{t('attendance.name') || 'Name'}</th><th style={S.th}>{t('attendance.date') || 'Date'}</th><th style={S.th}>{t('attendance.status') || 'Status'}</th><th style={S.th}>{t('attendance.hoursWorked') || 'Hours'}</th></tr></thead>
                          <tbody>
                            {br.branch_managers.map((bm, i) => (
                              <tr key={i}><td style={S.td}>{bm.name}</td><td style={S.td}>{bm.date}</td><td style={S.td}><StatusPill status={bm.status} /></td><td style={S.td}>{bm.hours_worked ?? '—'}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {(!br.workers?.length && !br.branch_managers?.length) && (
                      <p style={{ color: T.muted, fontSize: '0.9rem' }}>{t('attendance.noRecords') || 'No records.'}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
}

export default function Attendance() {
  return <ProtectedRoute requireSalonManager><AttendanceContent /></ProtectedRoute>;
}
